import {
  isHuafendaClassicX87Equipment,
  isHuafendaM98Equipment,
} from "./hfdGifLightingDevices";
import { compareFirmwareVersions } from "./compareFirmwareVersions";
import { isHuafendaDk108Device } from "./hfdDk108";
import { isHuafendaDk63StarDevice } from "./hfdDk63Star";
import { isHuafendaX75GcDevice } from "./hfdX75Gc";
import {
  isHfdFef9DongleProductId,
  isHfdFef9DongleX87NeonIdentifier,
  isHfdFef9DongleX87V2,
} from "./hfdFef9Dongle";

/** HFD X87 V2 的 VID+PID 列表（有线 + 2.4G） */
export const HFD_X87V2_VENDOR_PRODUCT: ReadonlyArray<{
  vendorId: number;
  productId: number;
  identifier: string;
}> = [
  { vendorId: 0x0c45, productId: 0x80bb, identifier: "3141:32955:X87 V2" }, // 有线
  { vendorId: 0x0c45, productId: 0xfef9, identifier: "3141:65273:2.4G Dongle" }, // 2.4G
];

export type HfdX87V2EquipmentLike = {
  vendorId?: number;
  productId?: number;
  keyboardType?: string;
  displayname?: string;
  productName?: string;
  solutionRovider?: string;
  identifier?: string;
};

/** 是否为 HFD X87 V2 设备（有线 + 2.4G） */
export function isHuafendaX87V2Device(
  eq: HfdX87V2EquipmentLike | null | undefined
): boolean {
  if (!eq) return false;

  const pid = Number(eq.productId);
  const id = String(eq.identifier ?? "").trim();
  const nameBlob = `${eq.displayname ?? ""} ${eq.keyboardType ?? ""} ${eq.productName ?? ""}`;

  if (pid === 0x80bb) return true;

  if (isHfdFef9DongleProductId(pid)) {
    return isHfdFef9DongleX87V2(eq);
  }

  // 与 Neon 同接收器 PID：仅当全量 id 为 …Dongle:3:10 时排除，不靠名称
  if (isHfdFef9DongleX87NeonIdentifier(id)) return false;
  if (/\bx87\s*v2\b/i.test(nameBlob) || id.includes("X87 V2")) return true;
  if (id.startsWith("3141:32955:")) return true;

  const vid = Number(eq.vendorId);
  if (!Number.isFinite(vid) || !id) return false;
  return HFD_X87V2_VENDOR_PRODUCT.some(
    (p) =>
      p.vendorId === vid &&
      (p.identifier === id || id.startsWith(`${p.identifier}:`))
  );
}

/** 是否为 HFD X87 V2 有线设备 */
export function isHuafendaX87V2Wired(
  eq: HfdX87V2EquipmentLike | null | undefined
): boolean {
  return isHuafendaX87V2Device(eq) && Number(eq?.productId) === 0x80bb;
}

/** 是否为 HFD X87 V2 2.4G 设备 */
export function isHuafendaX87V2Wireless(
  eq: HfdX87V2EquipmentLike | null | undefined
): boolean {
  return isHuafendaX87V2Device(eq) && isHfdFef9DongleProductId(eq?.productId);
}

/**
 * UI「关闭灯效」哨兵值。HID mode 为 uint8：9999 & 0xFF === 15（涟漪扩散），
 * 经典 X87 / X87 V2 / X75GC / M98 / M98 Screen / DK108 下发前须映射为设备关灯 mode=0。
 */
export const HFD_UI_OFF_LIGHTING_MODE = 9999;
/** 华奋达设备侧关灯效 mode */
export const HFD_X87V2_DEVICE_OFF_LIGHTING_MODE = 0;

/** 经典 X87 / X87 V2 / X75GC / M98 / M98 Screen / DK108：UI 关灯 9999 ↔ 设备 mode=0 */
export function needsHfdUiOffLightingModeMapping(
  eq: HfdX87V2EquipmentLike | null | undefined
): boolean {
  return (
    isHuafendaX87V2Device(eq) ||
    isHuafendaClassicX87Equipment(eq) ||
    isHuafendaX75GcDevice(eq) ||
    isHuafendaM98Equipment(eq) ||
    isHuafendaDk108Device(eq) ||
    isHuafendaDk63StarDevice(eq)
  );
}

/** 华奋达：UI mode → 设备 mode（仅处理关灯 9999→0） */
export function toHfdX87V2DeviceLightingMode(mode: number): number {
  return Number(mode) === HFD_UI_OFF_LIGHTING_MODE
    ? HFD_X87V2_DEVICE_OFF_LIGHTING_MODE
    : mode;
}

/** 华奋达：设备 mode → UI mode（仅处理关灯 0→9999） */
export function toHfdX87V2UiLightingMode(mode: number): number {
  return Number(mode) === HFD_X87V2_DEVICE_OFF_LIGHTING_MODE
    ? HFD_UI_OFF_LIGHTING_MODE
    : mode;
}

/** X87 V2 灯带修改所需最低固件版本（appVersion） */
export const HFD_X87_V2_LIGHT_STRIP_MIN_FIRMWARE = "1.72";

/** 已知固件版本低于门槛时返回 true；版本未知或为空时不拦截 */
export function isHfdX87V2LightStripFirmwareBlocked(
  firmwareVersion: string | null | undefined
): boolean {
  const core = String(firmwareVersion ?? "").trim();
  if (!core) return false;
  return compareFirmwareVersions(core, HFD_X87_V2_LIGHT_STRIP_MIN_FIRMWARE) < 0;
}
